import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button, Input, Skeleton, cn } from '@design-system';
import type { ReplyListEntry } from '../api/api';
import {
  useImportLibrarySeed,
  useReplyLibrary,
  useReplyListFilters,
  type ReplyFilter,
} from '../hooks/use-magic-email-library';
import { GoLivePanel } from './GoLivePanel';
import { StatusPill } from './StatusPill';

export const replyPath = (entryId: string) =>
  `/wizard/magic-email/library/${encodeURIComponent(entryId)}`;

const FILTERS: { value: ReplyFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'draft', label: 'Drafts' },
  { value: 'unavailable', label: 'Not available yet' },
];

const edited = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'Edited today';
  if (days === 1) return 'Edited yesterday';
  return `Edited ${new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
};

function SummaryCard({ label, count, note }: { label: string; count: number; note: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-zinc-200 bg-white px-5 py-4">
      <span className="text-[13px] text-zinc-600">{label}</span>
      <span className="text-[26px] font-bold">
        {count} {count === 1 ? 'reply' : 'replies'}
      </span>
      <span className="text-[13px] text-zinc-600">{note}</span>
    </div>
  );
}

/** Fewer example emails than this, and emails worded differently often miss the reply. */
const FEW_EXAMPLES = 3;

function ReplyRow({ reply }: { reply: ReplyListEntry }) {
  const unavailable = reply.status === 'unavailable';
  return (
    <Link
      to={replyPath(reply.entryId)}
      className={cn(
        'flex flex-wrap items-center gap-4 rounded-xl px-5 py-[18px] text-zinc-900 no-underline hover:border-[#5B3FD6]/40 hover:no-underline',
        unavailable
          ? 'border border-dashed border-zinc-300 bg-zinc-50'
          : 'border border-zinc-200 bg-white',
      )}
    >
      <div className="flex min-w-0 flex-[999_1_420px] flex-col gap-1">
        <span className={cn('text-base font-semibold', unavailable && 'text-zinc-700')}>
          {reply.title}
        </span>
        <span className="line-clamp-2 text-sm text-zinc-600">
          {unavailable
            ? reply.problems.find(problem => problem.fix === 'waterlily')?.message
            : `“${reply.summary}”`}
        </span>
      </div>
      {reply.status === 'live' ? (
        <StatusPill tone="live">Live</StatusPill>
      ) : unavailable ? (
        <StatusPill tone="unavailable">Not available yet</StatusPill>
      ) : (
        <StatusPill tone="draft">{reply.publishedVersion ? 'Off' : 'Draft'}</StatusPill>
      )}
      {!unavailable && reply.exampleCount < FEW_EXAMPLES && (
        <span
          className="rounded-full bg-[#FFF4E5] px-2 py-0.5 text-[12px] font-semibold text-[#8A4B00]"
          title="The assistant finds a reply by its example emails. Add more in its settings, or from the Test tab."
        >
          {reply.exampleCount === 1 ? '1 example' : `${reply.exampleCount} examples`}
        </span>
      )}
      {!unavailable && (
        <span className="text-[13px] text-zinc-500">{edited(reply.updatedDateTime)}</span>
      )}
    </Link>
  );
}

/** Every reply the assistant may send, by topic, with what state each is in. */
export function ReplyLibrary() {
  const library = useReplyLibrary();
  const list = useReplyListFilters(library.data?.entries);
  const importSeed = useImportLibrarySeed();

  if (library.isPending) return <Skeleton className="h-96 w-full" />;
  if (library.isError)
    return (
      <p className="text-sm text-red-700">Could not load the replies: {library.error.message}</p>
    );

  if (library.data.entries.length === 0) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-zinc-300 bg-white p-8">
        <h2 className="text-lg font-semibold">No replies yet</h2>
        <p className="text-sm text-zinc-600">
          Load the reviewed reply library to start. Nothing is sent until a reply is published.
        </p>
        <Button
          onClick={() => importSeed.mutate()}
          disabled={importSeed.isPending}
        >
          {importSeed.isPending ? 'Loading…' : 'Load the reply library'}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          label="Live"
          count={list.counts.live}
          note="The assistant can choose these today"
        />
        <SummaryCard
          label="Drafts"
          count={list.counts.draft}
          note="Ready to review and publish"
        />
        <SummaryCard
          label="Not available yet"
          count={list.counts.unavailable}
          note="Need something Waterlily can’t do by email yet"
        />
      </div>

      <GoLivePanel entries={library.data.entries} />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex h-11 flex-[1_1_360px] items-center gap-2.5 rounded-[10px] border border-zinc-300 bg-white px-3.5">
          <Search
            className="size-[18px] text-zinc-500"
            aria-hidden
          />
          <span className="sr-only">Search replies</span>
          <Input
            value={list.search}
            onChange={event => list.setSearch(event.target.value)}
            placeholder="Search by question or topic, e.g. “intake” or “quotes”"
            className="h-auto border-0 p-0 text-[15px] shadow-none focus-visible:ring-0"
          />
        </label>
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label="Show"
        >
          {FILTERS.map(filter => (
            <button
              key={filter.value}
              type="button"
              aria-pressed={list.status === filter.value}
              onClick={() => list.setStatus(filter.value)}
              className={cn(
                'h-11 rounded-full border px-4 text-sm',
                list.status === filter.value
                  ? 'border-[#5B3FD6] bg-[#F1EDFF] font-semibold text-[#4527B8]'
                  : 'border-zinc-300 bg-white font-medium text-zinc-700',
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {list.groups.length === 0 ? (
        <p className="text-sm text-zinc-600">No replies match.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {list.groups.map(group => (
            <section
              key={group.category}
              className="flex flex-col gap-2.5"
            >
              <h2 className="mt-3 text-[13px] font-semibold uppercase tracking-[0.04em] text-zinc-600 first:mt-0">
                {group.category}
              </h2>
              {group.replies.map(reply => (
                <ReplyRow
                  key={reply.entryId}
                  reply={reply}
                />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
